import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Grid, FormControl,
  InputLabel, Select, MenuItem, Chip, IconButton, Switch, FormControlLabel,
  CircularProgress, Snackbar, Alert, Collapse, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import {
  Add, Remove, ArrowBack, Save, Download, ExpandMore, ExpandLess,
  Star, StarBorder, ContentPaste,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getRecipe, createRecipe, updateRecipe } from '../../utils/firebase';
import { CATEGORIES, TAGS, DIFFICULTY, API_BASE } from '../../config';
import { v4 as uuidv4 } from 'uuid';

const UNITS = ['', 'g', 'kg', 'ml', 'L', 'cup', 'tbsp', 'tsp', 'piece', 'slice', 'clove', 'pinch', 'oz', 'lb', 'can'];

const defaultIngredient = () => ({ id: uuidv4(), name: '', amount: '', unit: '' });
const defaultInstruction = (step) => ({ step, text: '' });

const defaultForm = {
  title: '',
  description: '',
  category: 'dinner',
  difficulty: 'medium',
  prepTime: '',
  cookTime: '',
  servings: '',
  ingredients: [defaultIngredient()],
  instructions: [defaultInstruction(1)],
  tags: [],
  status: 'bookmarked',
  starred: false,
  notes: '',
  sourceUrl: '',
  photos: [],
  coverImage: null,
};

export default function AddEditRecipePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [importPasteMode, setImportPasteMode] = useState(false);
  const [pasteContent, setPasteContent] = useState('');
  const [ingPasteOpen, setIngPasteOpen] = useState(false);
  const [ingPasteText, setIngPasteText] = useState('');

  useEffect(() => {
    if (isEdit) {
      loadRecipe(); // eslint-disable-line react-hooks/exhaustive-deps
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRecipe = async () => {
    try {
      const data = await getRecipe(id);
      if (!data) {
        navigate('/recipes');
        return;
      }
      setForm({
        ...defaultForm,
        ...data,
        prepTime: data.prepTime ?? '',
        cookTime: data.cookTime ?? '',
        servings: data.servings ?? '',
        ingredients: data.ingredients?.length > 0 ? data.ingredients : [defaultIngredient()],
        instructions: data.instructions?.length > 0 ? data.instructions : [defaultInstruction(1)],
      });
    } catch (err) {
      showSnackbar('Failed to load recipe', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImport = async () => {
    if (!importUrl && !pasteContent) return;
    setImporting(true);
    try {
      const body = importPasteMode
        ? { content: pasteContent, url: importUrl }
        : { url: importUrl };

      const res = await fetch(`${API_BASE}/api/rb/import-recipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (data.error === 'fetch_failed') {
        setImportPasteMode(true);
        showSnackbar(data.message, 'warning');
        return;
      }

      if (data.recipe) {
        const r = data.recipe;
        setForm((prev) => ({
          ...prev,
          title: r.title || prev.title,
          description: r.description || prev.description,
          category: r.category || prev.category,
          difficulty: r.difficulty || prev.difficulty,
          prepTime: r.prepTime ?? prev.prepTime,
          cookTime: r.cookTime ?? prev.cookTime,
          servings: r.servings ?? prev.servings,
          ingredients: r.ingredients?.length > 0
            ? r.ingredients.map((i) => ({ ...i, id: uuidv4() }))
            : prev.ingredients,
          instructions: r.instructions?.length > 0
            ? r.instructions
            : prev.instructions,
          tags: r.tags?.length > 0 ? r.tags : prev.tags,
          sourceUrl: importUrl || prev.sourceUrl,
          notes: importUrl ? `Source: ${importUrl}` : prev.notes,
        }));
        setImportOpen(false);
        showSnackbar('Recipe imported! Review and save.');
      }
    } catch (err) {
      showSnackbar('Import failed. Is the server running?', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showSnackbar('Please add a title', 'warning');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...form,
        prepTime: Number(form.prepTime) || 0,
        cookTime: Number(form.cookTime) || 0,
        servings: Number(form.servings) || 0,
        ingredients: form.ingredients.filter((i) => i.name.trim()),
        instructions: form.instructions
          .filter((i) => i.text.trim())
          .map((i, idx) => ({ ...i, step: idx + 1 })),
      };

      if (isEdit) {
        await updateRecipe(id, data);
        showSnackbar('Recipe saved!');
        navigate(`/recipes/${id}`);
      } else {
        const newId = await createRecipe(user.uid, data);
        showSnackbar('Recipe created!');
        navigate(`/recipes/${newId}`);
      }
    } catch (err) {
      showSnackbar('Failed to save recipe', 'error');
    } finally {
      setSaving(false);
    }
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // Ingredients
  const updateIngredient = (index, field, value) => {
    setForm((prev) => {
      const ingredients = [...prev.ingredients];
      ingredients[index] = { ...ingredients[index], [field]: value };
      return { ...prev, ingredients };
    });
  };
  const addIngredient = () => setForm((prev) => ({ ...prev, ingredients: [...prev.ingredients, defaultIngredient()] }));
  const removeIngredient = (index) => setForm((prev) => ({
    ...prev,
    ingredients: prev.ingredients.filter((_, i) => i !== index),
  }));

  // Instructions
  const updateInstruction = (index, value) => {
    setForm((prev) => {
      const instructions = [...prev.instructions];
      instructions[index] = { ...instructions[index], text: value };
      return { ...prev, instructions };
    });
  };
  const addInstruction = () => setForm((prev) => ({
    ...prev,
    instructions: [...prev.instructions, defaultInstruction(prev.instructions.length + 1)],
  }));
  const removeInstruction = (index) => setForm((prev) => ({
    ...prev,
    instructions: prev.instructions.filter((_, i) => i !== index).map((inst, i) => ({ ...inst, step: i + 1 })),
  }));

  // Smart ingredient paste parser
  const parseIngredientLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const UNIT_RE = /^([\d\s¼½¾⅓⅔⅛⅜⅝⅞\/\.]+)?\s*(g|kg|ml|l|cups?|tbsps?|tablespoons?|tsps?|teaspoons?|pieces?|slices?|cloves?|pinches?|oz|lbs?|pounds?|cans?|sticks?|handfuls?|bunches?|sprigs?|sheets?)\.?\s*/i;
    const match = trimmed.match(UNIT_RE);
    if (match) {
      const amountRaw = (match[1] || '').trim();
      const unitRaw = (match[2] || '').trim().toLowerCase();
      const name = trimmed.slice(match[0].length).trim().replace(/^of\s+/i, '');
      const unitMap = { tablespoon: 'tbsp', tablespoons: 'tbsp', teaspoon: 'tsp', teaspoons: 'tsp',
        cup: 'cup', cups: 'cup', pound: 'lb', pounds: 'lb', l: 'L' };
      const unit = unitMap[unitRaw] || unitRaw;
      if (name) return { id: uuidv4(), amount: amountRaw, unit, name };
    }
    // No unit — might be "3 eggs" or just "salt"
    const numMatch = trimmed.match(/^([\d¼½¾⅓⅔⅛⅜⅝⅞\/\.]+)\s+(.+)/);
    if (numMatch) return { id: uuidv4(), amount: numMatch[1], unit: '', name: numMatch[2].trim() };
    return { id: uuidv4(), amount: '', unit: '', name: trimmed };
  };

  const handleIngPasteParse = () => {
    const lines = ingPasteText.split(/[\n,;]+/).map((l) => l.trim()).filter(Boolean);
    const parsed = lines.map(parseIngredientLine).filter(Boolean);
    if (parsed.length > 0) {
      setForm((prev) => ({
        ...prev,
        ingredients: [
          ...prev.ingredients.filter((i) => i.name.trim()),
          ...parsed,
        ],
      }));
    }
    setIngPasteText('');
    setIngPasteOpen(false);
  };

  // Tags
  const toggleTag = (tag) => setForm((prev) => ({
    ...prev,
    tags: prev.tags.includes(tag) ? prev.tags.filter((t) => t !== tag) : [...prev.tags, tag],
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 }, pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5">{isEdit ? 'Edit Recipe' : 'Add Recipe'}</Typography>
      </Box>

      {/* Import from URL */}
      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Button
          fullWidth
          onClick={() => setImportOpen(!importOpen)}
          endIcon={importOpen ? <ExpandLess /> : <ExpandMore />}
          sx={{
            justifyContent: 'space-between',
            px: 2.5,
            py: 1.5,
            color: 'text.primary',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            🔗 Import from URL
          </Typography>
        </Button>
        <Collapse in={importOpen}>
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste a recipe URL and let AI extract the recipe for you.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                placeholder="https://example.com/recipe"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                fullWidth
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                onKeyDown={(e) => e.key === 'Enter' && !importPasteMode && handleImport()}
              />
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={importing || (!importUrl && !pasteContent)}
                startIcon={importing ? <CircularProgress size={16} /> : <Download />}
                sx={{ flexShrink: 0, background: 'linear-gradient(135deg, #f0873c, #c96820)' }}
              >
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </Box>
            {importPasteMode && (
              <Box>
                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                  Could not fetch URL directly. Paste the recipe text below:
                </Typography>
                <TextField
                  multiline
                  rows={6}
                  fullWidth
                  placeholder="Paste the recipe text here..."
                  value={pasteContent}
                  onChange={(e) => setPasteContent(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Basic Info */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Basic Info</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Recipe Title"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              fullWidth
              multiline
              rows={4}
              placeholder="Brief description of the dish..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} onChange={(e) => setField('category', e.target.value)} label="Category">
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.emoji} {c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Difficulty</InputLabel>
              <Select value={form.difficulty} onChange={(e) => setField('difficulty', e.target.value)} label="Difficulty">
                {DIFFICULTY.map((d) => (
                  <MenuItem key={d.value} value={d.value} sx={{ color: d.color }}>{d.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Prep Time (min)"
              type="number"
              value={form.prepTime}
              onChange={(e) => setField('prepTime', e.target.value)}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Cook Time (min)"
              type="number"
              value={form.cookTime}
              onChange={(e) => setField('cookTime', e.target.value)}
              fullWidth
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Servings"
              type="number"
              value={form.servings}
              onChange={(e) => setField('servings', e.target.value)}
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Ingredients */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">🧾 Ingredients</Typography>
          <Button size="small" startIcon={<ContentPaste />} onClick={() => setIngPasteOpen(true)}
            sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>
            Paste list
          </Button>
        </Box>
        {form.ingredients.map((ing, i) => (
          <Box key={ing.id} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
            <TextField
              label="Amount"
              value={ing.amount}
              onChange={(e) => updateIngredient(i, 'amount', e.target.value)}
              sx={{ width: 90, flexShrink: 0 }}
              size="small"
            />
            <FormControl size="small" sx={{ width: 90, flexShrink: 0 }}>
              <InputLabel>Unit</InputLabel>
              <Select
                value={ing.unit}
                onChange={(e) => updateIngredient(i, 'unit', e.target.value)}
                label="Unit"
              >
                {UNITS.map((u) => (
                  <MenuItem key={u} value={u}>{u || '—'}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Ingredient name"
              value={ing.name}
              onChange={(e) => updateIngredient(i, 'name', e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. all-purpose flour"
            />
            <IconButton
              size="small"
              onClick={() => removeIngredient(i)}
              disabled={form.ingredients.length === 1}
              sx={{ color: 'error.main', flexShrink: 0 }}
            >
              <Remove fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button startIcon={<Add />} onClick={addIngredient} size="small" sx={{ color: 'primary.main', mt: 0.5 }}>
          Add Ingredient
        </Button>
      </Paper>

      {/* Instructions */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>📋 Instructions</Typography>
        {form.instructions.map((inst, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 32, height: 32, borderRadius: '50%',
                bgcolor: 'primary.dark', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, fontWeight: 700, fontSize: '0.85rem', mt: 0.5,
              }}
            >
              {i + 1}
            </Box>
            <TextField
              multiline
              minRows={2}
              value={inst.text}
              onChange={(e) => updateInstruction(i, e.target.value)}
              fullWidth
              placeholder={`Step ${i + 1}...`}
            />
            <IconButton
              size="small"
              onClick={() => removeInstruction(i)}
              disabled={form.instructions.length === 1}
              sx={{ color: 'error.main', flexShrink: 0, mt: 0.5 }}
            >
              <Remove fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Button startIcon={<Add />} onClick={addInstruction} size="small" sx={{ color: 'primary.main' }}>
          Add Step
        </Button>
      </Paper>

      {/* Tags & Status */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Tags & Status</Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Tags</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
          {TAGS.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              clickable
              onClick={() => toggleTag(tag)}
              variant={form.tags.includes(tag) ? 'filled' : 'outlined'}
              color={form.tags.includes(tag) ? 'secondary' : 'default'}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={form.status} onChange={(e) => setField('status', e.target.value)} label="Status">
              <MenuItem value="bookmarked">📌 Bookmarked</MenuItem>
              <MenuItem value="made">✅ Made It</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={form.starred}
                onChange={(e) => setField('starred', e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {form.starred ? <Star sx={{ color: 'warning.main', fontSize: 18 }} /> : <StarBorder sx={{ fontSize: 18 }} />}
                <Typography variant="body2">Starred / Must-Try</Typography>
              </Box>
            }
          />
        </Box>
      </Paper>

      {/* Notes & Source */}
      <Paper sx={{ p: 2.5, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Notes & Source</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Personal Notes"
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              fullWidth
              multiline
              rows={5}
              placeholder="Tips, variations, memory of when you made it..."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Source URL"
              value={form.sourceUrl}
              onChange={(e) => setField('sourceUrl', e.target.value)}
              fullWidth
              placeholder="https://..."
              helperText="Original recipe URL or Instagram link"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Save Button */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderColor: 'rgba(255,255,255,0.2)' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)', px: 4 }}
        >
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Recipe'}
        </Button>
      </Box>

      {/* Ingredient paste dialog */}
      <Dialog open={ingPasteOpen} onClose={() => setIngPasteOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: 'rgba(22,18,14,0.98)' } }}>
        <DialogTitle>Paste Ingredient List</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste ingredients one per line (e.g. "2 cups flour", "1 tsp salt"). Commas and semicolons also work as separators.
          </Typography>
          <TextField
            multiline rows={8} fullWidth autoFocus
            placeholder={"2 cups all-purpose flour\n1 tsp baking powder\n3 eggs\n200ml milk"}
            value={ingPasteText}
            onChange={(e) => setIngPasteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIngPasteOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleIngPasteParse} disabled={!ingPasteText.trim()}
            sx={{ background: 'linear-gradient(135deg, #f0873c, #c96820)' }}>
            Parse & Add
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
